import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, Brain, EyeOff, Sparkles, ShieldCheck, Zap, Cpu, Target, Quote, CheckCircle2, XCircle, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import LaunchWaitlistModal from "@/components/marketing/LaunchWaitlistModal";
import { isLaunchDismissed, markLaunchDismissed } from "@/lib/launch";
import CostCalculatorModal from "@/components/marketing/CostCalculatorModal";
import CitePopover from "@/components/marketing/CitePopover";
import { RESEARCH } from "@/lib/content";

function AnimatedCounter({ to, suffix = "", duration = 1.6 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let started = false;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) {
          started = true;
          const start = performance.now();
          const animate = (now) => {
            const t = Math.min(1, (now - start) / (duration * 1000));
            setVal(Math.floor(to * (1 - Math.pow(1 - t, 3))));
            if (t < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      });
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to, duration]);
  return (
    <span ref={ref} className="font-display font-bold tabular-nums">
      {val.toLocaleString()}
      {suffix}
    </span>
  );
}

function Hero() {
  return (
    <section
      className="relative overflow-hidden min-h-[92vh] flex items-center"
      data-testid="hero"
    >
      <div
        className="hero-blob"
        style={{ top: -160, left: -160 }}
      />
      <div
        className="hero-blob"
        style={{
          bottom: -200,
          right: -200,
          background:
            "radial-gradient(circle at 40% 40%, rgba(44,107,172,0.6), rgba(26,58,92,0.15) 50%, transparent 75%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#E8ECF2] bg-white/60 backdrop-blur px-3 py-1.5 mb-6"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#1D9E75]" />
            <span className="text-xs uppercase tracking-[0.18em] text-[#2C6BAC] font-semibold">
              Now in private beta · EU & UK
            </span>
          </motion.div>

          <h1
            className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#1A1A2E]"
            data-testid="hero-headline"
          >
            Get back to <br />
            <span className="text-[#1A3A5C]">deep focus.</span>
          </h1>

          <p className="mt-7 text-lg text-[#555568] max-w-xl leading-relaxed">
            WYLO reduces the cost of interruptions and diversions so you can do your best work, every time.
            Built employee-first. Privacy as a product value, not a checkbox.
          </p>

          <div className="mt-10 flex items-center gap-6 text-xs text-[#555568]">
            <div className="flex items-center gap-2">
              <ShieldCheck size={14} className="text-[#1D9E75]" />
              <span>GDPR · EU AI Act ready</span>
            </div>
            <div className="flex items-center gap-2">
              <EyeOff size={14} className="text-[#1A3A5C]" />
              <span>Privacy = Priority. Always!</span>
            </div>
          </div>
        </div>

        {/* Floating mockup card (static) */}
        <div className="lg:col-span-5 relative">
          <FocusCardMock />
          <div className="mt-16 flex flex-wrap justify-center lg:justify-end gap-3" data-testid="hero-cta-group">
            <Link to="/features" data-testid="hero-cta-features">
              <Button variant="outline" className="h-12 px-6 border-[#1A3A5C] text-[#1A1A2E] hover:bg-[#E8ECF2]">
                See Features
              </Button>
            </Link>
            <Link to="/register" data-testid="hero-cta-trial">
              <Button className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white h-12 px-6 relative overflow-hidden btn-shimmer">
                Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function FocusCardMock() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 bg-gradient-to-br from-[#2C6BAC]/10 via-transparent to-[#1A3A5C]/5 rounded-3xl blur-2xl" />
      <div className="relative bg-white rounded-2xl border border-[#E8ECF2] p-6 shadow-[0_20px_60px_rgba(26,58,92,0.12)]">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#2C6BAC] font-semibold">Today's focus</p>
            <p className="font-display text-2xl font-bold text-[#1A1A2E] mt-1">Tuesday, 14 May</p>
          </div>
          <div className="h-12 w-12 rounded-full border-2 border-[#1D9E75] flex items-center justify-center">
            <span className="font-mono font-bold text-[#1D9E75]">82</span>
          </div>
        </div>

        <div className="space-y-3">
          <Row title="Priority: Figma - Q3 Brand System" value="2h 14m" pct="62%" tone="success" />
          <Row title="Notion - Roadmap doc" value="48m" pct="22%" tone="success" />
          <Row title="Slack interruption" value="6m" pct="3%" tone="amber" />
          <Row title="X.com" value="11m" pct="5%" tone="danger" />
        </div>

        <div className="mt-5 pt-5 border-t border-[#E8ECF2] flex items-center justify-between">
          <div>
            <p className="text-xs text-[#555568] uppercase tracking-wider">Refocus avg</p>
            <p className="font-mono text-lg text-[#1A3A5C]">02:18</p>
          </div>
          <div className="flex -space-x-1">
            <span className="h-7 w-7 rounded-full bg-[#1D9E75] border-2 border-white" />
            <span className="h-7 w-7 rounded-full bg-[#BA7517] border-2 border-white" />
            <span className="h-7 w-7 rounded-full bg-[#E24B4A] border-2 border-white" />
          </div>
        </div>
      </div>

      {/* Refocus widget preview floating */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.6 }}
        className="absolute -bottom-8 -right-4 sm:-right-10 bg-white/80 backdrop-blur-xl border border-white/60 rounded-xl p-4 w-56 shadow-[0_12px_40px_rgba(26,58,92,0.18)] soft-pulse"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="h-9 w-9 rounded-md bg-[#1A3A5C] flex items-center justify-center">
            <span className="text-white text-xs font-bold">Fg</span>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#2C6BAC] font-semibold">Refocus</p>
            <p className="text-sm font-semibold text-[#1A1A2E] -mt-0.5">Brand System v2</p>
          </div>
        </div>
        <p className="text-xs text-[#555568] font-mono">⏱ 02:14 ago</p>
      </motion.div>
    </div>
  );
}

function Row({ title, value, pct, tone }) {
  const tones = {
    success: "bg-[#1D9E75]",
    amber: "bg-[#BA7517]",
    danger: "bg-[#E24B4A]",
  };
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <p className="text-[#1A1A2E]">{title}</p>
        <span className="font-mono text-xs text-[#555568]">{value}</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-[#E8ECF2] overflow-hidden">
        <div className={`h-full ${tones[tone]}`} style={{ width: pct }} />
      </div>
    </div>
  );
}

function ProblemSection() {
  const [calcOpen, setCalcOpen] = useState(false);
  return (
    <section className="py-24 lg:py-32 bg-[#F8F9FC]" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">
            The hidden tax on focus
          </p>
          <h2 className="font-display text-4xl lg:text-5xl tracking-tighter text-[#1A1A2E] leading-[1.05]">
            Real-world field studies suggest it takes <span className="text-[#E24B4A]">20+ minutes</span> to fully regain deep focus after a single interruption.<CitePopover item={RESEARCH[0]} />
          </h2>
          <p className="mt-6 text-[#555568] text-lg leading-relaxed">
            Scaled across a 500-person organisation, that recovery time adds up to millions in lost productivity every year, not to mention a quiet engine of burnout.
            WYLO solves the problem no notification blocker can touch.
          </p>
          <Link to="/resources#research" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2C6BAC] hover:text-[#1A3A5C]" data-testid="problem-read-more">
            Read more of the research behind WYLO <ArrowRight size={14} />
          </Link>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-2 gap-4 sm:gap-6">
            {RESEARCH.map((r) => <StatCard key={r.id} item={r} highlight={r.id === 4} />)}
          </div>

          <div className="mt-10 flex justify-center">
            <button
              type="button"
              onClick={() => setCalcOpen(true)}
              data-testid="open-cost-calculator"
              className="cta-orbit group inline-flex items-center gap-2.5 rounded-full bg-[#1A3A5C] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-transform hover:-translate-y-0.5 active:translate-y-0"
            >
              <Calculator size={16} className="text-[#7DE3B4]" />
              See Your Own Numbers
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
      <CostCalculatorModal open={calcOpen} onOpenChange={setCalcOpen} />
    </section>
  );
}

function StatCard({ item, highlight }) {
  return (
    <div
      data-testid={`stat-card-${item.id}`}
      className={`relative p-5 sm:p-7 rounded-xl border flex flex-col ${
        highlight
          ? "bg-[#1A3A5C] text-white border-[#1A3A5C]"
          : "bg-white text-[#1A1A2E] border-[#E8ECF2]"
      }`}
    >
      <div className={`font-display tracking-tight leading-none ${item.suffix.length > 3 ? "text-3xl lg:text-4xl" : "text-3xl sm:text-4xl lg:text-5xl"}`}>
        <span className="whitespace-nowrap">{item.prefix}<AnimatedCounter to={item.value} suffix={item.suffix} /><CitePopover item={item} dark={highlight} /></span>
      </div>
      <p className={`mt-3 text-sm flex-1 ${highlight ? "text-[#E8ECF2]/80" : "text-[#555568]"}`}>{item.label}</p>
      <Link
        to={`/resources?cite=${item.id}#research`}
        data-testid={`stat-read-more-${item.id}`}
        className={`mt-4 inline-flex items-center gap-1 text-xs font-semibold ${highlight ? "text-[#7DE3B4] hover:text-white" : "text-[#2C6BAC] hover:text-[#1A3A5C]"}`}
      >
        Read more <ArrowRight size={12} />
      </Link>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    {
      icon: Brain,
      title: "WYLO learns your priorities",
      body: "Tell us the websites and apps that matter. WYLO's software quietly learns the cadence of your best work to understand your optimal workflow.",
    },
    {
      icon: Target,
      title: "A different FACE detected",
      body: "A Focus or Attention Changing Event (FACE) is our bedrock. Anything from a colleague drop-in, a Slack ping, or a doom-scroll detour will automatically trigger a refocus, or trigger it yourself with hotkey options.",
    },
    {
      icon: Sparkles,
      title: "We bring you straight back",
      body: "A calm, glanceable refocus card shows you exactly where you were. Context restored, instantly!",
    },
  ];
  return (
    <section id="how-it-works" className="py-24 lg:py-32 bg-white border-y border-[#E8ECF2]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">
            How WYLO works
          </p>
          <h2 className="font-display text-4xl lg:text-5xl tracking-tighter text-[#1A1A2E] leading-[1.05]">
            A three-step ritual<br />for returning to focus.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12, duration: 0.5 }}
              className="relative p-8 rounded-xl border border-[#E8ECF2] bg-[#F8F9FC] hover:-translate-y-1 transition-transform"
              data-testid={`how-step-${i}`}
            >
              <div className="font-mono text-xs text-[#2C6BAC]">0{i + 1}</div>
              <div className="mt-4 h-12 w-12 rounded-md bg-[#1A3A5C] text-white flex items-center justify-center">
                <s.icon size={20} />
              </div>
              <h3 className="font-display text-xl font-bold text-[#1A1A2E] mt-5">{s.title}</h3>
              <p className="text-sm text-[#555568] mt-3 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AISection() {
  return (
    <section className="py-24 lg:py-32 bg-[#F8F9FC]">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">
          AI + Human work
        </p>
        <h2 className="font-display text-4xl lg:text-5xl tracking-tighter text-[#1A1A2E] leading-[1.1]">
          AI should amplify human potential.<br />Not replace it.
        </h2>
        <p className="mt-8 text-lg text-[#555568] max-w-3xl mx-auto leading-relaxed">
          WYLO is built on the belief that every insight should help people work better, and help organisations understand their teams more deeply, without surveillance. AI works with derived signals and metadata only - privacy is top priority!
        </p>
        <div className="mt-10 inline-flex flex-wrap items-center justify-center gap-2">
          <Pill>Privacy by design</Pill>
          <Pill>EU data residency</Pill>
          <Pill>Opt-out of AI modelling</Pill>
        </div>
        <div className="mt-8">
          <Link to="/our-story" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2C6BAC] hover:text-[#1A3A5C]" data-testid="ai-read-more">
            Read more - Our story <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-[#E8ECF2] bg-white px-3 py-1.5 text-xs font-medium text-[#1A1A2E]">
      <CheckCircle2 size={12} className="text-[#1D9E75]" />
      {children}
    </span>
  );
}

function TierPreview() {
  const tiers = [
    { name: "Spark", price: "Free", desc: "14-day web trial", color: "border-[#E8ECF2]" },
    { name: "Focus Web", price: "$14", desc: "Web monitoring · 1 user", color: "border-[#E8ECF2]" },
    { name: "Focus", price: "$23", desc: "Web + 3 local apps", color: "border-[#2C6BAC]", featured: true },
    { name: "Sync", price: "$28", desc: "3–24 users", color: "border-[#E8ECF2]" },
    { name: "Momentum", price: "$38", desc: "25–100 users", color: "border-[#E8ECF2]" },
    { name: "Command", price: "Custom", desc: "Enterprise · 101+", color: "border-[#1A3A5C]" },
  ];
  return (
    <section className="py-24 lg:py-32 bg-white border-y border-[#E8ECF2]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Plans</p>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tighter text-[#1A1A2E]">
              From solo focus to organisation-wide insight.
            </h2>
          </div>
          <Link to="/pricing">
            <Button variant="outline" className="border-[#1A3A5C] text-[#1A1A2E]">
              See full pricing <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {tiers.map((t, i) => (
            <div
              key={i}
              className={`rounded-xl border ${t.color} p-5 ${t.featured ? "bg-[#1A3A5C] text-white tracing-beam" : "bg-white text-[#1A1A2E]"}`}
              data-testid={`tier-preview-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <p className="font-display text-xs uppercase tracking-[0.18em] opacity-70">WYLO</p>
              <h3 className="font-display text-xl font-bold mt-1">{t.name}</h3>
              <p className="font-mono text-2xl mt-4">{t.price}</p>
              <p className={`text-xs mt-2 ${t.featured ? "text-[#E8ECF2]/80" : "text-[#555568]"}`}>{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CompetitorStrip() {
  const rows = [
    { name: "RescueTime", point: "tracks time. WYLO works to restore focus and get you back on track." },
    { name: "Reclaim.ai", point: "protects your calendar. WYLO protects your attention." },
    { name: "Focusmate", point: "requires partner availability. WYLO works solo or as a team - have it your way." },
  ];
  return (
    <section className="py-20 bg-[#F8F9FC]" data-testid="compared-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">
          Compared
        </p>
        <h2 className="font-display text-3xl lg:text-4xl tracking-tighter text-[#1A1A2E] mb-10">
          What makes WYLO different.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {rows.map((r, i) => (
            <div key={i} className="p-6 rounded-xl bg-white border border-[#E8ECF2] hover:-translate-y-1 transition-transform" data-testid={`compared-${r.name.toLowerCase().replace(/\W/g, "")}`}>
              <p className="font-display text-lg font-bold text-[#1A1A2E]">{r.name}</p>
              <p className="text-sm text-[#555568] mt-2 leading-relaxed">
                <span className="font-semibold text-[#1A1A2E]">{r.name}</span> {r.point}
              </p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-[#555568]" data-testid="compared-complement">
          WYLO may be a good complement to any of these solutions if you already have them.
        </p>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-24 lg:py-32 bg-[#1A3A5C] text-white relative overflow-hidden">
      <div className="hero-blob" style={{ top: -300, left: "30%", opacity: 0.18 }} />
      <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
        <h2 className="font-display text-4xl lg:text-6xl tracking-tighter leading-[1.05]">
          Ready to get back<br />to your real work?
        </h2>
        <p className="mt-7 text-[#E8ECF2]/80 max-w-xl mx-auto">
          Try it out for free. 14 days free, No credit card. You'll know within the first session.
        </p>
        <div className="mt-9">
          <Link to="/register" data-testid="final-cta-trial">
            <Button className="bg-white text-[#1A3A5C] hover:bg-[#E8ECF2] h-12 px-8">
              Start Free Trial <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [showPopup, setShowPopup] = useState(() => !isLaunchDismissed());
  const dismiss = () => {
    markLaunchDismissed();
    setShowPopup(false);
  };
  return (
    <MarketingLayout>
      <Hero />
      <ProblemSection />
      <AISection />
      <HowItWorks />
      <TierPreview />
      <CompetitorStrip />
      <FinalCTA />
      {showPopup && <LaunchWaitlistModal onClose={dismiss} />}
    </MarketingLayout>
  );
}
